import { formatInr, formatInrCompact, type FormatInrOptions } from "./currency";

export type FormatPctOptions = {
  /** Minimum revenue (INR) before showing a margin-style percentage. */
  minRevenue?: number;
  /** Minimum tickets sold before showing sell-through. */
  minTicketsSold?: number;
  /** Cap displayed magnitude (default 999). */
  maxMagnitude?: number;
  /** When below significance floor, return this (default "—"). */
  emptyLabel?: string;
  /** Basis for margin calculations — used for tooltip context only. */
  basis?: "margin" | "sell-through" | "generic";
};

const DEFAULT_MIN_REVENUE = 25_000;
const DEFAULT_MIN_TICKETS = 20;
const DEFAULT_MAX_MAGNITUDE = 999;

/** Re-export with spec naming; dashboards use no paise decimals. */
export function formatINR(value: number, options?: FormatInrOptions): string {
  return formatInr(value, options);
}

export function formatINRCompact(value: number): string {
  return formatInrCompact(value);
}

function belowSignificanceFloor(value: number, options?: FormatPctOptions): boolean {
  if (options?.basis === "sell-through" && options.minTicketsSold !== undefined) {
    return false;
  }
  if (options?.minRevenue !== undefined && options.minRevenue > 0) {
    return false;
  }
  return false;
}

/**
 * Percentage formatter with significance floors and magnitude cap (Section 8.1).
 * Returns "—" when data is too thin to be meaningful.
 */
export function formatPct(
  value: number | null | undefined,
  options?: FormatPctOptions & { revenue?: number; ticketsSold?: number },
): string {
  const empty = options?.emptyLabel ?? "—";
  if (value === null || value === undefined || !Number.isFinite(value)) return empty;

  const minRevenue = options?.minRevenue ?? (options?.basis === "margin" ? DEFAULT_MIN_REVENUE : 0);
  const minTickets = options?.minTicketsSold ?? (options?.basis === "sell-through" ? DEFAULT_MIN_TICKETS : 0);

  if (options?.basis === "margin" && (options.revenue ?? 0) < minRevenue) return empty;
  if (options?.basis === "sell-through" && (options.ticketsSold ?? 0) < minTickets) return empty;

  if (belowSignificanceFloor(value, options) && options?.basis === "generic") {
    return empty;
  }

  const maxMag = options?.maxMagnitude ?? DEFAULT_MAX_MAGNITUDE;
  const clamped = Math.max(-maxMag, Math.min(maxMag, value));
  return `${clamped.toFixed(1)}%`;
}

export function formatMarginPct(
  value: number | null | undefined,
  revenue?: number,
  options?: Omit<FormatPctOptions, "basis">,
): string {
  return formatPct(value, { ...options, basis: "margin", revenue, minRevenue: options?.minRevenue ?? DEFAULT_MIN_REVENUE });
}

export function formatSellThroughPct(
  value: number | null | undefined,
  ticketsSold?: number,
  options?: Omit<FormatPctOptions, "basis">,
): string {
  return formatPct(value, {
    ...options,
    basis: "sell-through",
    ticketsSold,
    minTicketsSold: options?.minTicketsSold ?? DEFAULT_MIN_TICKETS,
  });
}

export function formatRelativeUpdated(date: Date | number | null | undefined): string {
  if (date === null || date === undefined) return "";
  const ts = typeof date === "number" ? date : date.getTime();
  if (!Number.isFinite(ts)) return "";
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Updated just now";
  if (diffMin < 60) return `Updated ${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `Updated ${diffHr} hr ago`;
  return `Updated ${new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

/** "just now" / "5 min ago" / "3 hr ago" / falls back to a short date beyond 24h. */
export function formatRelativeTime(date: Date | number | string | null | undefined): string {
  if (date === null || date === undefined) return "";
  const ts = typeof date === "string" ? new Date(date).getTime() : typeof date === "number" ? date : date.getTime();
  if (!Number.isFinite(ts)) return "";
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatDisplayDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function formatChipDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
